
import React from 'react';
import { ArrowRight, Palette, ShoppingBag, Briefcase, Heart, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const Index = () => {
  const linktreeTypes = [
    { 
      id: 'tech', 
      name: 'Tech/Digital Agency', 
      description: 'Design futurista com gradientes vibrantes',
      icon: Sparkles,
      color: 'from-blue-500 to-purple-600'
    },
    { 
      id: 'ecommerce', 
      name: 'E-commerce/Retail', 
      description: 'Visual comercial elegante',
      icon: ShoppingBag,
      color: 'from-emerald-500 to-blue-500'
    },
    { 
      id: 'consulting', 
      name: 'Consultoria/Serviços', 
      description: 'Profissional e minimalista',
      icon: Briefcase,
      color: 'from-slate-600 to-slate-800'
    },
    { 
      id: 'health', 
      name: 'Saúde/Wellness', 
      description: 'Clean e confiável',
      icon: Heart,
      color: 'from-teal-500 to-blue-500'
    },
    { 
      id: 'creative', 
      name: 'Criativo/Design', 
      description: 'Artístico e vibrante',
      icon: Palette,
      color: 'from-pink-500 to-purple-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 antialiased">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-8">
              Linktrees Profissionais
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed">
              5 designs únicos e modernos para diferentes tipos de negócios.
              Transforme sua presença digital com estilo e profissionalismo.
            </p>
            
            <Link
              to="/linktrees"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              Explorar Linktrees
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Preview Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {linktreeTypes.map((type, index) => (
            <div
              key={type.id}
              className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
            >
              <div className={`w-16 h-16 bg-gradient-to-r ${type.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <type.icon className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {type.name}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {type.description}
              </p>
              
              <Link
                to="/linktrees"
                className="inline-flex items-center text-blue-600 font-semibold hover:text-purple-600 transition-colors"
              >
                Ver exemplo
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Recursos Profissionais
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Cada Linktree foi cuidadosamente projetado com foco na experiência do usuário
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Design Responsivo</h3>
              <p className="text-gray-600">Perfeito em qualquer dispositivo, do mobile ao desktop</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Otimizado para Conversão</h3>
              <p className="text-gray-600">Layouts que incentivam a ação do usuário</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Palette className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Identidade Visual</h3>
              <p className="text-gray-600">Cada design reflete o estilo do seu negócio</p>
            </div>
          </div>
        </div>
      </div>

      {/* Decide Digital Signature */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Pronto para criar seu Linktree?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Escolha o design que melhor representa seu negócio e comece a impressionar seus clientes
          </p>
          
          <Link
            to="/linktrees"
            className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 mb-8"
          >
            Explorar Todos os Designs
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>

          {/* Decide Digital Signature */}
          <div className="border-t border-blue-500/30 pt-8 mt-8">
            <p className="text-blue-100 text-lg font-medium mb-2">
              💙 Desenvolvido com excelência pela
            </p>
            <h3 className="text-2xl font-bold text-white mb-3">
              Decide Digital
            </h3>
            <p className="text-blue-200 text-sm max-w-md mx-auto">
              Agência especializada em soluções digitais inovadoras | Cacoal - RO
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
